/**
 * Laabha — top-level app shell.
 *
 * Layout:
 *   ┌──────────┬────────────────────────────────────┐
 *   │ Sessions │  Header (status + settings)        │
 *   │   list   ├────────────────────────────────────┤
 *   │          │  Conversation (messages + composer)│
 *   └──────────┴────────────────────────────────────┘
 *
 * Keyboard shortcuts:
 *   Cmd-K       New conversation
 *   Cmd-,       Open settings
 *   Cmd-Enter   Submit composer (handled inside Composer.tsx)
 *   Esc         Close any open modal
 */

import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Settings as SettingsIcon, Plus, LogOut } from 'lucide-react';
import { conversationsApi } from '@/lib/api';
import { saveLocalConversation } from '@/lib/conversationCache';
import { useKeyboardShortcut, useSettings } from '@/lib/hooks';
import { getAccessToken, getCachedUser, isJwtValid, refreshZitadelToken } from '@/lib/auth';
import { SessionList } from '@/components/chat/SessionList';
import { Conversation } from '@/components/chat/Conversation';
import { Settings } from '@/components/Settings';
import { MappingRulesEditor } from '@/components/MappingRulesEditor';
import { TallyStatus } from '@/components/TallyStatus';
import { Login } from '@/components/Login';
import { AuthCallback } from '@/components/AuthCallback';
import { startZitadelLogout } from '@/lib/zitadel-oidc';
import { BalancePill } from '@/components/BalancePill';
import { Recharge } from '@/components/Recharge';
import { billingApi } from '@/lib/billing';

const STORAGE_KEY = 'axondesktop.activeConversationId';

type AuthState = 'checking' | 'logged_in' | 'logged_out';

export default function App() {
  const qc = useQueryClient();
  const [settings] = useSettings();
  const [activeId, setActiveId] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY),
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [mappingRulesOpen, setMappingRulesOpen] = useState(false);
  const [authState, setAuthState] = useState<AuthState>(() =>
    getAccessToken() ? 'logged_in' : 'logged_out',
  );

  // Balance shared with header + Recharge modal + Conversation
  const { data: balance } = useQuery({
    queryKey: ['billing-balance'],
    queryFn: () => billingApi.getBalance(),
    refetchInterval: 60_000,
    retry: false,
    enabled: authState === 'logged_in',
  });

  // BLL fires "balance_depleted" central rejection → auto-pop recharge modal.
  // The depletion event is dispatched by lib/aiaccountant.ts whenever an
  // agent call returns an insufficient-credits error code.
  useEffect(() => {
    const onDepleted = () => {
      setRechargeOpen(true);
      qc.invalidateQueries({ queryKey: ['billing-balance'] });
    };
    window.addEventListener('axondesktop.billing.depleted', onDepleted);
    return () =>
      window.removeEventListener('axondesktop.billing.depleted', onDepleted);
  }, [qc]);

  // On startup: if a token exists, show the app immediately — no server round-trip.
  // The gateway's Zitadel introspection credentials are not configured yet, so any
  // server-side auth check will fail for OIDC users. We trust localStorage:
  //   token present  → logged_in (shown instantly, no flicker to login page)
  //   token absent   → logged_out (show login)
  // Background: if the JWT is expired, attempt a silent Zitadel refresh.
  // Auth state never changes to logged_out from this path — only an explicit
  // clearAuth() call (sign-out button or a future 401-interceptor) logs the user out.
  useEffect(() => {
    if (authState !== 'logged_in') return;

    // Silent background refresh — never blocks or changes auth state to logged_out.
    if (!isJwtValid()) {
      refreshZitadelToken().catch(() => {});
    }

    // React to clearAuth() or setAuth() called anywhere in the app.
    const onAuthChange = () => {
      setAuthState(getAccessToken() ? 'logged_in' : 'logged_out');
    };
    window.addEventListener('axondesktop.auth.changed', onAuthChange);
    return () =>
      window.removeEventListener('axondesktop.auth.changed', onAuthChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeId) localStorage.setItem(STORAGE_KEY, activeId);
    else localStorage.removeItem(STORAGE_KEY);
  }, [activeId]);

  const { data } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => conversationsApi.list(),
    retry: false,
    enabled: authState === 'logged_in',
  });

  useEffect(() => {
    if (!activeId && data?.conversations?.length) {
      setActiveId(data.conversations[0].id);
    }
  }, [data, activeId]);

  const createMut = useMutation({
    mutationFn: () => conversationsApi.create(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      setActiveId(res.conversation.id);
    },
    onError: () => {
      // Gateway returned 403 (no company_id mapping yet) — create locally so
      // the button always works. AIAccountant import flows use public routes
      // and don't need a server-persisted conversation ID.
      const now = new Date().toISOString();
      const localConv = {
        id: crypto.randomUUID(),
        title: 'New Conversation',
        created_at: now,
        updated_at: now,
      };
      saveLocalConversation(localConv);
      qc.invalidateQueries({ queryKey: ['conversations'] });
      setActiveId(localConv.id);
    },
  });

  const newChat = useCallback(() => createMut.mutate(), [createMut]);
  const openSettings = useCallback(() => setSettingsOpen(true), []);

  // Cmd-K → new chat
  useKeyboardShortcut('cmd+k', newChat);
  // Cmd-, → settings
  useKeyboardShortcut('cmd+,', openSettings);
  // Esc → close modal
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSettingsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Auth gate
  // Handle the OIDC callback first — `/desktop/auth/callback?code=…` lands here
  // before the regular auth check fires.
  if (window.location.pathname === '/desktop/auth/callback') {
    return <AuthCallback onSuccess={() => setAuthState('logged_in')} />;
  }
  if (authState === 'checking') {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-sm text-muted-foreground">Checking session…</div>
      </div>
    );
  }
  if (authState === 'logged_out') {
    return <Login onLoggedIn={() => setAuthState('logged_in')} />;
  }

  return (
    <div className="h-screen w-screen flex bg-background text-foreground">
      <SessionList
        activeId={activeId}
        onSelect={setActiveId}
        onCreated={setActiveId}
      />
      <main className="flex-1 flex flex-col min-w-0">
        <Header
          onNewChat={newChat}
          onOpenSettings={openSettings}
          onOpenRecharge={() => setRechargeOpen(true)}
        />
        {activeId ? (
          <Conversation
            conversationId={activeId}
            defaultSlash={settings.default_slash || undefined}
            balance={balance ?? null}
            onOpenRecharge={() => setRechargeOpen(true)}
            onOpenSettings={openSettings}
            onOpenMappingRules={() => setMappingRulesOpen(true)}
          />
        ) : (
          <EmptyMain onStart={newChat} />
        )}
      </main>

      <Settings open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <Recharge
        open={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        balance={balance ?? null}
      />
      <MappingRulesEditor
        open={mappingRulesOpen}
        companyId={settings.company_id || ''}
        onClose={() => setMappingRulesOpen(false)}
      />
    </div>
  );
}

function Header({
  onNewChat,
  onOpenSettings,
  onOpenRecharge,
}: {
  onNewChat: () => void;
  onOpenSettings: () => void;
  onOpenRecharge: () => void;
}) {
  const user = getCachedUser();
  const hasToken = !!getAccessToken();
  const userLabel = user?.email ?? 'your account';
  return (
    <header className="h-14 border-b border-border flex items-center px-4 gap-3 bg-card shrink-0">
      <img
        src="/desktop/laabha.png"
        alt="Laabha"
        className="h-7 w-auto"
        draggable={false}
      />
      <div className="ml-auto flex items-center gap-2">
        {user?.email && (
          <span className="text-xs text-muted-foreground hidden sm:inline">{user.email}</span>
        )}
        <BalancePill onClickRecharge={onOpenRecharge} />
        <TallyStatus onOpenSettings={onOpenSettings} />
        <button
          onClick={onNewChat}
          className="p-1.5 rounded hover:bg-muted text-muted-foreground"
          title="New conversation (⌘K)"
          aria-label="New conversation"
        >
          <Plus className="w-4 h-4" />
        </button>
        <button
          onClick={onOpenSettings}
          className="p-1.5 rounded hover:bg-muted text-muted-foreground"
          title="Settings (⌘,)"
          aria-label="Settings"
        >
          <SettingsIcon className="w-4 h-4" />
        </button>
        {hasToken && (
          <button
            onClick={() => {
              if (confirm(`Sign out ${userLabel}?`)) {
                startZitadelLogout();
              }
            }}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground"
            title={`Sign out ${userLabel}`}
            aria-label="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        )}
      </div>
    </header>
  );
}

function EmptyMain({ onStart }: { onStart: () => void }) {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center max-w-md">
        <img
          src="/desktop/laabha.png"
          alt="Laabha"
          className="h-10 w-auto mx-auto mb-4"
          draggable={false}
        />
        <h1 className="text-lg font-medium mb-1">Welcome to Laabha</h1>
        <p className="text-sm text-muted-foreground mb-4">
          Configure your Tally ngrok URL in <kbd className="font-mono bg-muted px-1.5 py-0.5 rounded">⌘,</kbd>{' '}
          and start a chat with{' '}
          <kbd className="font-mono bg-muted px-1.5 py-0.5 rounded">⌘K</kbd>.
        </p>
        <button
          onClick={onStart}
          className="px-4 py-2 text-sm rounded bg-primary text-primary-foreground hover:bg-primary/90"
        >
          New conversation
        </button>
      </div>
    </div>
  );
}
